import { BaseField } from '@epages/react-components'
import { DndContext } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import type { List, Map } from 'immutable'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { useContext } from 'react'
import { useSelector } from 'react-redux'

import type { DraggableRefProps } from '../../utils/hooks/useSortableHelpers'
import { ExposeIsDragging, useSortableA11y, useSortableSensors } from '../../utils/hooks/useSortableHelpers'
import { ListItem } from './ListItem'
import { useTranslate } from '../../utils/translate'
import FormValue from '../formComponents/FormValue'

export default function LegalPagesPopover({ draggableRef }: DraggableRefProps) {
  const interfaceT = useTranslate('interface', 'components.bottomBar.footerSettings.sections.popovers.pageSelection')

  const isBeyondShop = useSelector<State, boolean>((state) => Boolean(state.getIn(['shop', 'beyond'])))
  const t = useTranslate('shop')
  const translatePage = (title: string) => (isBeyondShop ? t(title) : title)

  const legalPages: Frontend.LegalPage[] = (useContext(BaseField.contextType) as any)?.instance
    ?.getValue('legalPages')
    ?.toJS()

  const a11y = useSortableA11y(interfaceT, (draggableId) =>
    translatePage(legalPages.find(({ id }) => id === draggableId)?.title || ''),
  )
  const sensors = useSortableSensors()

  return (
    <FormValue name="legalPages">
      {(legalPages: List<Map<string, keyof Core.LegalPage>>, setLegalPages) => {
        function handleDragEnd({ active, over }: DragEndEvent) {
          if (!over || over.id === active.id) return
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const oldIndex = legalPages.findIndex((page) => page!.get('id') === active.id)
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          const newIndex = legalPages.findIndex((page) => page!.get('id') === over.id)
          setLegalPages(legalPages.remove(oldIndex).splice(newIndex, 0, legalPages.get(oldIndex)))
        }

        return (
          <DndContext
            accessibility={a11y}
            sensors={sensors}
            modifiers={[restrictToFirstScrollableAncestor, restrictToVerticalAxis]}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              items={legalPages.map((page) => page!.get('id')).toArray()}
              strategy={verticalListSortingStrategy}
            >
              <ExposeIsDragging draggableRef={draggableRef} />
              <div className="ep-footer-menu">
                <fieldset>
                  <legend>{interfaceT('title')}</legend>
                  <scroll-shadow>
                    <div className="ep-footer-sections-settings-popover-column">
                      {legalPages
                        .map((page: ImmutableMap, index: number) => (
                          <ListItem key={page.get('id')} id={page.get('id')} index={index} name="legalPages" />
                        ))
                        .toArray()}
                    </div>
                  </scroll-shadow>
                </fieldset>
              </div>
            </SortableContext>
          </DndContext>
        )
      }}
    </FormValue>
  )
}

LegalPagesPopover.defaultValue = {}
